"use client"
import { useFlags } from "@/lib/external/feature-flags/feature-flags-context";
import { Button } from "@external/landing/components/ui/button";
import { motion } from "framer-motion";
import "@/components/i18n/i18n";
import { useTranslation } from "@/components/i18n/next";
import { useEffect, useState } from "react";
import { features } from "process";
import { fetchWithAuth } from "@external/landing/lib/utils";
import {
  translateIntervalUnit,
  translatePlanDescription,
  translatePlanFeature,
  translatePlanName,
} from "@/lib/subscription-i18n";

type SubscriptionSectionProps = {
    loginNoSubscription?: boolean | null;
    title?: string | null;
}
const SubscriptionSection = ({ loginNoSubscription, title }: SubscriptionSectionProps) => {
    const { t } = useTranslation();
    const flags = useFlags();
    const [subscriptionPlans, setSubscriptionPlans] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const subscribeClickHandler = (planId: string) => {
        window.location.href = `/subscribe/${planId}`;
    }

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await fetchWithAuth("/api/public/plans", {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    credentials: "include",
                });

                const plans = await response.json();
                console.log(plans);
                // Show plans that have either PayPal or Stripe support
                setSubscriptionPlans(plans.filter((plan: any) => !!plan.paypal_registry || !!plan.stripe_price_id || !!plan.stripe_plan_id));
                setLoading(false);
            } catch (err) {
                console.error("Failed to fetch subscription plans:", err);
                setError("Failed to load subscription plans");
                setLoading(false);
            }
        };

        fetchPlans();
    }, []);

    if (loading) {
        return (
            <div className="flex flex-col w-full items-center justify-center py-16">
                <div className="text-white opacity-70">{t("Loading subscription plans...")}</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col w-full items-center justify-center py-16">
                <div className="text-white opacity-70">{t(error)}</div>
            </div>
        );
    }

    console.log(subscriptionPlans);
    return (
        <div className="flex flex-col w-full lg:px-5 md:px-5 px-14 opacity-90" id="subscribe">
            <section className="lg:py-16 md:py-16 pt-16 pb-0">
                <div className="flex flex-col items-center text-center lg:mb-10 md:mb-10 mb-10">
                    {<div className="relative inline-block lg:mb-4 md:mb-4 mb-6 font-pilat">
                        <span
                            className="subscription-title lg:text-[42px] md:text-[42px] text-[28px] absolute inset-0 text-transparent font-bold top-[-14px] opacity-20">
                            {title ? t(title) : t("PREMIUM ACCESS")}
                        </span>
                        <span
                            className="relative lg:text-[42px] md:text-[42px] text-[28px] subscription-title text-primary font-bold">
                            {title ? t(title) : t("PREMIUM ACCESS")}
                        </span>
                    </div>}
                    <motion.div
                        className="max-w-2xl mx-auto"
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        <div className="text-white lg:text-sm md:text-sm text-xs flex flex-wrap gap-x-1 gap-y-1 max-w-xl justify-center leading-snug">
                            {loginNoSubscription ? <>
                                <span className="opacity-70">{t("Premium access is required to access this section.")}</span>
                            </> : <>
                                <span className="opacity-70">{t("Elevate your betting strategy with our premium plans.")}</span>
                                <span className="opacity-70">{t("Get exclusive access to AI-powered selections and expert insights.")}</span>
                            </>}
                        </div>
                    </motion.div>
                </div>

                <div className="lg:grid-cols-1 lg:grid-cols-2 lg:grid-cols-3"></div>
                <div className={`flex lg:flex-row md:flex-row flex-col w-full lg:gap-8 md:gap-8 gap-6 container mx-auto justify-center max-w-5xl items-center`}>
                    {subscriptionPlans.map((plan, index) => (
                        <motion.div
                            key={plan.key}
                            className={` ${index === 1 ? 'rookie-plan' : index === 2 ? 'king-plan' : 'gradient-bg '} ${plan.recommended ? 'border border-primary' : 'border'} rounded-[20px] lg:mb-0 md:mb-0 mb-3 transition-all duration-300 glow-hover relative w-[320px]`}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            viewport={{ once: true }}
                        >

                            <div className="lg:p-6 md:p-6 pt-6 px-0 pb-12 flex flex-col items-center justify-center">
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        width: '100%',
                                        height: '100%',
                                        background: `linear-gradient(
                                              180deg,
                                              rgba(0, 0, 0, 0.4) 30%,
                                              rgba(0, 0, 0, 0.15) 30%,
                                              rgba(238, 221, 67, 0.1) 100%
                                            ),
                                            url("/images/plan${index}.png")`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                        // opacity: 0.4,
                                        borderRadius: '20px',
                                        zIndex: 0,
                                        pointerEvents: 'none'
                                    }}
                                />
                                <h3 className={`${plan.recommended ? "border-1 border-border text-primary" : ""} text-xl font-pilat tracking-[1px] font-bold pb-3 opacity-90`}>{translatePlanName(t, plan)}</h3>
                                <p className="text-xs text-[rgba(255, 255, 255, 0.60)] opacity-70 pb-8">{translatePlanDescription(t, plan)}</p>
                                <ul className=" w-full px-6 text-left space-y-3 lg:min-h-48 md:min-h-48 opacity-90">
                                    {plan.features.map((feature: {
                                        key: string,
                                        name: string,
                                        active: boolean,
                                    }) => {
                                        return (
                                            <li key={feature.key} className={`flex items-left ${feature.active ? "opacity-100" : "opacity-40"}`} style={{ color: 'white' }}>
                                                {feature.active ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                                        viewBox="0 0 14 14" fill="none">
                                                        <path
                                                            d="M10.0733 5.00365C10.1233 5.05366 10.1631 5.11305 10.1901 5.17842C10.2172 5.24378 10.2312 5.31385 10.2312 5.38461C10.2312 5.45538 10.2172 5.52544 10.1901 5.59081C10.1631 5.65618 10.1233 5.71557 10.0733 5.76558L6.30404 9.53481C6.25403 9.58487 6.19465 9.62459 6.12928 9.65168C6.06391 9.67878 5.99384 9.69273 5.92308 9.69273C5.85232 9.69273 5.78225 9.67878 5.71688 9.65168C5.65151 9.62459 5.59213 9.58487 5.54212 9.53481L3.92673 7.91942C3.8257 7.81838 3.76893 7.68135 3.76893 7.53846C3.76893 7.39557 3.8257 7.25854 3.92673 7.1575C4.02777 7.05646 4.16481 6.9997 4.30769 6.9997C4.45058 6.9997 4.58762 7.05646 4.68866 7.1575L5.92308 8.3926L9.31135 5.00365C9.36136 4.95359 9.42074 4.91387 9.48611 4.88677C9.55148 4.85968 9.62155 4.84573 9.69231 4.84573C9.76307 4.84573 9.83314 4.85968 9.89851 4.88677C9.96388 4.91387 10.0233 4.95359 10.0733 5.00365ZM14 7C14 8.38447 13.5895 9.73785 12.8203 10.889C12.0511 12.0401 10.9579 12.9373 9.67879 13.4672C8.3997 13.997 6.99224 14.1356 5.63437 13.8655C4.2765 13.5954 3.02922 12.9287 2.05026 11.9497C1.07129 10.9708 0.404603 9.7235 0.134506 8.36563C-0.13559 7.00776 0.00303299 5.6003 0.532846 4.32122C1.06266 3.04213 1.95987 1.94888 3.11101 1.17971C4.26216 0.410543 5.61553 0 7 0C8.85592 0.00195988 10.6353 0.740087 11.9476 2.05242C13.2599 3.36475 13.998 5.14409 14 7ZM12.9231 7C12.9231 5.82853 12.5757 4.68336 11.9249 3.70931C11.274 2.73527 10.349 1.97609 9.26667 1.52779C8.18436 1.07949 6.99343 0.96219 5.84447 1.19073C4.6955 1.41928 3.64011 1.98339 2.81175 2.81175C1.9834 3.64011 1.41928 4.6955 1.19074 5.84446C0.962193 6.99343 1.07949 8.18436 1.52779 9.26666C1.9761 10.349 2.73527 11.274 3.70932 11.9249C4.68336 12.5757 5.82853 12.9231 7 12.9231C8.57035 12.9213 10.0759 12.2967 11.1863 11.1863C12.2967 10.0759 12.9213 8.57035 12.9231 7Z"
                                                            fill="white" />
                                                    </svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                                        viewBox="0 0 14 14" fill="none">
                                                        <path
                                                            d="M14 7C14 8.38447 13.5895 9.73785 12.8203 10.889C12.0511 12.0401 10.9579 12.9373 9.67879 13.4672C8.3997 13.997 6.99224 14.1356 5.63437 13.8655C4.2765 13.5954 3.02922 12.9287 2.05026 11.9497C1.07129 10.9708 0.404603 9.7235 0.134506 8.36563C-0.13559 7.00776 0.00303299 5.6003 0.532846 4.32122C1.06266 3.04213 1.95987 1.94888 3.11101 1.17971C4.26216 0.410543 5.61553 0 7 0C8.85592 0.00195988 10.6353 0.740087 11.9476 2.05242C13.2599 3.36475 13.998 5.14409 14 7ZM12.9231 7C12.9231 5.82853 12.5757 4.68336 11.9249 3.70931C11.274 2.73527 10.349 1.97609 9.26667 1.52779C8.18436 1.07949 6.99343 0.96219 5.84447 1.19073C4.6955 1.41928 3.64011 1.98339 2.81175 2.81175C1.9834 3.64011 1.41928 4.6955 1.19074 5.84446C0.962193 6.99343 1.07949 8.18436 1.52779 9.26666C1.9761 10.349 2.73527 11.274 3.70932 11.9249C4.68336 12.5757 5.82853 12.9231 7 12.9231C8.57035 12.9213 10.0759 12.2967 11.1863 11.1863C12.2967 10.0759 12.9213 8.57035 12.9231 7Z"
                                                            fill="white" />
                                                        <path d="M4 7H10" stroke="white" stroke-opacity="0.8" stroke-width="1.4" stroke-linecap="round" />
                                                    </svg>
                                                )}

                                                <span className={`text-xs text-white font-medium pl-2`}>{translatePlanFeature(t, feature)}</span>
                                            </li>
                                        )
                                    })}
                                </ul>
                            </div>
                            <div className="h-[80px]"></div>
                            <div className={`px-6 py-4 flex flex-col items-center absolute left-0 right-0 bottom-4 ${plan.recommended ? "text-primary" : ""}`}>
                                <div className="flex  items-baseline my-1">
                                    <p className="text-3xl flex font-medium my-1">{plan.currency_symbol}{plan.price}</p>/
                                    <p>{translateIntervalUnit(t, plan.interval_unit)}</p>
                                </div>
                            </div>
                            <div className="absolute left-0 right-0 m-auto w-[140px] text-center bottom-[-20px]">
                                <Button className={` rounded-full px-6 py-2 text-xs font-normal shadow-lg ${plan.recommended
                                    ? "recommended-button hover:border-primary text-primary hover:transform hover:scale-105 transition-all duration-300"
                                    : "bg-background text-white border border-border hover:bg-background hover:text-primary hover:border-primary hover:transform hover:scale-105 transition-all duration-300"
                                    }`}
                                    onClick={() => subscribeClickHandler(plan.key)}
                                >
                                    {flags.pre_launch ? t("Coming soon") : loginNoSubscription ? t("Upgrade account") : t("Subscribe")}
                                </Button>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

        </div>
    );
};

export default SubscriptionSection;
